/**
 * ports/replays.ts
 *
 * Saved replays of completed puzzles, stored on the device. Each carries the
 * move log plus enough of the puzzle (size, colors, name) to play back with
 * no network, and the local profile id and time it was solved.
 */
import type { MoveCommand } from '../engine/history';

import type { PuzzleGivens } from '@nonogram/shared';

export interface ReplayPlayer {
  id: string;
  name: string;
  color: string;
  /** This device's player. */
  self?: boolean;
}

export interface ReplaySummary {
  id: string;
  puzzleId: string;
  puzzleName: string;
  rows: number;
  columns: number;
  colors: string[];
  solveTimeMs: number;
  /** Epoch ms. */
  completedAt: number;
  mode: 'solo' | 'multiplayer';
  /** Local profile id of the player who saved it. */
  profileId: string;
  moveCount: number;
  /** Multiplayer: who took part, with their colors at completion. */
  players?: ReplayPlayer[];
  /** Server id once uploaded for sharing (Phase 5). */
  sharedId?: string;
  /** Cells the puzzle revealed at the start; the replay starts from them. */
  givens?: PuzzleGivens;
}

export interface ReplayRecord extends ReplaySummary {
  moveLog: MoveCommand[];
}

export interface ReplayStorePort {
  saveReplay(replay: ReplayRecord): Promise<void>;
  getReplay(id: string): Promise<ReplayRecord | null>;
  /** Every replay without its move log, newest first. */
  listReplays(): Promise<ReplaySummary[]>;
  deleteReplay(id: string): Promise<void>;
  clearReplays(): Promise<void>;
}

export function toReplaySummary(record: ReplayRecord): ReplaySummary {
  const { moveLog: _moveLog, ...summary } = record;
  return summary;
}
